// sentReferences — the chip row under a SENT author message (D7, D8).
//
// An @-mention lives INLINE in the text (`[title](/library/{id})`, D8) and
// renders as a chip at its position, so the row holds only the references
// with no position: attached files, and every reference of a legacy message
// written before D8 (its text carries no tokens). ONE projection for both
// shapes an author entry reaches the view in: live it carries `mentions`
// (the composer's snapshots, with any upload `status`); reloaded it carries
// `referencedIds` (the durable `resourceRefs`, ids only, which include the
// inline ones). Every id resolves through the library, so a renamed resource
// shows its current title; an id the library no longer knows falls back to
// the live snapshot when there is one (an upload still landing) and is
// otherwise dropped. Pure.

import * as Core from "@bilda/core";
import { toComposerResourceRef } from "../../app/AppShellHost/constants.js";
import type { AuthorEntry } from "../../components/Chat/types.js";
import type {
    ComposerAttachmentStatus,
    ComposerResourceRef,
} from "../../components/Composer/PowerComposer/types.js";
import type {
    ResourceId,
    ResourceRef,
} from "../../components/Navigation/types.js";

export const sentReferences = (
    entry: AuthorEntry,
    refFor: (id: ResourceId) => ResourceRef | undefined,
    // A landed source's live read status as a chip decoration (FR-3.3).
    landedStatusOf: (
        id: ResourceId,
    ) => ComposerAttachmentStatus | undefined = () => undefined,
): readonly ComposerResourceRef[] => {
    const snapshots = entry.mentions ?? [];
    const inline: ReadonlySet<string> = new Set(
        Core.ProseLink.mentionedResourceIds(entry.text),
    );
    const ids = [
        ...snapshots.map((m) => m.id),
        ...(entry.referencedIds ?? []),
    ].filter((id) => !inline.has(id));
    const resolve = (id: ResourceId): readonly ComposerResourceRef[] => {
        const ref = refFor(id);
        const snapshot = snapshots.find((m) => m.id === id);
        if (ref === undefined) return snapshot === undefined ? [] : [snapshot];
        // Resolved ⇒ landed: the snapshot's send-time status is frozen and
        // would pulse until reload (G14), so a landed reference shows only its
        // LIVE read status, and rests status-free once readable (D57).
        const status = landedStatusOf(id);
        const chip = toComposerResourceRef(ref);
        return [status === undefined ? chip : { ...chip, status }];
    };
    return ids.filter((id, i) => ids.indexOf(id) === i).flatMap(resolve);
};
